// Variant picker and gallery for the product page. Without JS the page still
// works: the form submits whichever variant the server rendered as selected.
(() => {
  const root = document.querySelector("[data-product]");
  if (!root) return;

  const variants = JSON.parse(root.querySelector("[data-variants]").textContent);
  const idInput = root.querySelector('input[name="id"]');
  const price = root.querySelector("[data-price]");
  const addButton = root.querySelector("[data-add]");
  const addLabel = root.querySelector("[data-add-label]");
  const stage = root.querySelector(".product__stage");
  const slides = [...root.querySelectorAll("[data-slide]")];
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const thumbs = [...root.querySelectorAll("[data-thumb]")];
  const groups = [...root.querySelectorAll("[data-option]")];

  // The photos sit side by side on a strip, the current one at `track` px. A step
  // slides over CSS; a swipe follows the finger and then lets go to the same slide.
  let index = Math.max(0, thumbs.findIndex((t) => t.getAttribute("aria-current") === "true"));
  const n = slides.length;

  const render = (track, slide) => {
    slides.forEach((el, i) => {
      let offset = (i - index + n) % n;
      if (offset > n / 2) offset -= n;
      const previous = Number(el.dataset.offset ?? offset);
      const moves = slide && !reduceMotion.matches && (Math.abs(offset) <= 1 || Math.abs(previous) <= 1);
      el.hidden = false;
      el.style.transition = moves ? "" : "none";
      el.style.transform = `translateX(calc(${offset * 100}% + ${track}px))`;
      el.style.visibility = Math.abs(offset) <= 1 ? "visible" : "hidden";
      el.dataset.offset = offset;
    });
    thumbs.forEach((t, i) => t.setAttribute("aria-current", String(i === index)));
  };

  const showThumb = (thumb) => {
    const i = thumbs.indexOf(thumb);
    if (i < 0 || i === index || !n) return;
    index = i;
    render(0, true);
  };

  if (n) render(0, false);

  thumbs.forEach((thumb) => thumb.addEventListener("click", () => showThumb(thumb)));

  // Arrows step through the same list as the thumbnails, wrapping at the ends.
  const prevButton = root.querySelector("[data-gallery-prev]");
  const nextButton = root.querySelector("[data-gallery-next]");
  if (prevButton && nextButton && thumbs.length > 1) {
    const step = (dir) => {
      index = (index + dir + n) % n;
      render(0, true);
    };
    prevButton.hidden = nextButton.hidden = false;
    prevButton.addEventListener("click", () => step(-1));
    nextButton.addEventListener("click", () => step(1));
  }

  // Touch swipe, and a horizontal trackpad swipe. Vertical moves still scroll the page.
  if (stage && n > 1) {
    let drag = null;
    stage.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "touch") return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, swiping: false };
    }, { passive: true });
    stage.addEventListener("pointermove", (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.swiping) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        if (Math.abs(dx) < 8) return;
        drag.swiping = true;
        stage.setPointerCapture(event.pointerId);
      }
      drag.dx = dx;
      render(dx, false);
    }, { passive: true });
    const release = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { dx, swiping } = drag;
      drag = null;
      if (!swiping) return;
      const w = stage.clientWidth;
      if (Math.abs(dx) > w * 0.2) index = (index + (dx < 0 ? 1 : -1) + n) % n;
      // Hand the strip back from where the finger left it so it keeps moving.
      slides.forEach((el) => { el.style.transition = "none"; });
      const from = Math.abs(dx) > w * 0.2 ? dx + (dx < 0 ? w : -w) : dx;
      render(from, false);
      void stage.offsetWidth;
      render(0, true);
    };
    stage.addEventListener("pointerup", release);
    stage.addEventListener("pointercancel", release);

    let lastSwipe = 0;
    stage.addEventListener("wheel", (event) => {
      if (Math.abs(event.deltaX) < 12 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      if (Date.now() - lastSwipe < 500) return;
      lastSwipe = Date.now();
      index = (index + (event.deltaX > 0 ? 1 : -1) + n) % n;
      render(0, true);
    }, { passive: true });
  }

  const onChange = () => {
    const chosen = groups.map((g) => g.querySelector("input:checked")?.value);
    const variant = variants.find((v) => v.options.every((o, i) => o === chosen[i]));

    addButton.disabled = !variant || !variant.available;
    addLabel.textContent = !variant ? "Unavailable" : variant.available ? "Add to cart" : "Sold out";
    if (!variant) return;

    idInput.value = variant.id;
    price.textContent = variant.price;
    if (variant.media) {
      showThumb(thumbs.find((t) => Number(t.dataset.mediaId) === variant.media));
    }

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    history.replaceState(null, "", url);
  };

  groups.forEach((g) => g.addEventListener("change", onChange));

  // Size chart: the trigger ships hidden and appears only where <dialog> works.
  const chart = root.querySelector("[data-chart]");
  const chartOpen = root.querySelector("[data-chart-open]");
  if (chart && chartOpen && typeof chart.showModal === "function") {
    chartOpen.hidden = false;

    // Mark the row for the size currently picked in the form.
    const markCurrent = () => {
      const size = chartOpen.closest("[data-option]").querySelector("input:checked")?.value.toLowerCase();
      chart.querySelectorAll("[data-chart-size]").forEach((row) => {
        row.setAttribute("aria-current", String(row.dataset.chartSize === size));
      });
    };

    chartOpen.addEventListener("click", () => {
      markCurrent();
      chart.showModal();
    });

    // The dialog has no padding, so a click whose target is the dialog itself
    // landed on the backdrop.
    chart.addEventListener("click", (e) => {
      if (e.target === chart || e.target.closest("[data-chart-close]")) chart.close();
    });
  }

  // Picks rail on phones: the caret steps one card, and at the end flips to go back.
  const rail = root.querySelector("[data-picks]");
  const railNext = root.querySelector("[data-picks-next]");
  if (rail && railNext && rail.children.length > 1) {
    const atEnd = () => rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
    const behavior = () => (reduceMotion.matches ? "auto" : "smooth");
    const markEnd = () => {
      railNext.toggleAttribute("data-at-end", atEnd());
      railNext.setAttribute("aria-label", atEnd() ? "Back to first tee" : "Next tees");
    };
    railNext.hidden = false;
    rail.addEventListener("scroll", markEnd, { passive: true });
    railNext.addEventListener("click", () => {
      if (atEnd()) {
        rail.scrollTo({ left: 0, behavior: behavior() });
        return;
      }
      const stride = rail.children[1].offsetLeft - rail.children[0].offsetLeft;
      rail.scrollBy({ left: stride, behavior: behavior() });
    });
  }

  // Picks on desktop: hovering a card rotates its photos, then returns to the first.
  // The extras load on first hover, and a photo only shows once it has loaded.
  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches && !reduceMotion.matches) {
    root.querySelectorAll("[data-photos]").forEach((pick) => {
      let layers = null;
      let shown = 0;
      let timer;
      const reset = () => {
        clearTimeout(timer);
        shown = 0;
        layers?.forEach((layer) => layer.classList.remove("is-on"));
      };
      const step = () => {
        if (shown === layers.length) {
          layers.forEach((layer) => layer.classList.remove("is-on"));
          shown = 0;
        } else if (layers[shown].complete) {
          layers[shown].classList.add("is-on");
          shown += 1;
        }
        timer = setTimeout(step, 1100);
      };
      const start = () => {
        if (!layers) {
          layers = pick.dataset.photos.split("|").filter(Boolean).map((src) => {
            const layer = new Image();
            layer.className = "pick__layer";
            layer.alt = "";
            layer.src = src;
            pick.querySelector(".pick__media").append(layer);
            return layer;
          });
        }
        reset();
        timer = setTimeout(step, 250);
      };
      pick.addEventListener("pointerenter", start);
      pick.addEventListener("pointerleave", reset);
      pick.addEventListener("focus", start);
      pick.addEventListener("blur", reset);
    });
  }
})();
